// Needs to be included somewhere in the project in order for TS to see the type definitions which extend the `TokenTypeMap` type
import 'micromark-extension-gfm-table'

import {parse, postprocess as micromarkPostprocess, preprocess} from 'micromark'
import {gfm} from 'micromark-extension-gfm'

import {isListType} from '../syntax-tree/utils'
import {replaceFinalLineEndingWithBreak} from './extensions/final-line-break'
import {createListItemTokens} from './extensions/list-items'
import type {Token} from './token'

const micromarkParse = parse({extensions: [gfm()]})

/** Apply our internal post-processing transformations. Currently fixes blockquotes and lists. */
function postprocessTokens(tokens: Token[]): Token[] {
  replaceFinalLineEndingWithBreak(tokens)

  // Insert tokens to target for decorations
  const processedTokens = createListItemTokens(tokens)

  // Shift tokens
  const shiftedTokens: Token[] = []
  for (const token of processedTokens) {
    if (token.type === 'blockQuote' && token.direction === 'enter') {
      // Nested blockquotes are parsed as a blockquote immediately preceded by the prefixes from the parent blockquote,
      // which results in a broken-feeling document structure for the user because the nested `<blockquote>` element would
      // start in between two prefix tokens. To work around this, we modify nested blockquotes to 'consume' any immediately
      // preceding prefixes, by expanding the start of the blockquote backward and inserting it before the prefix.
      consumePrecedingTokens(shiftedTokens, token, 'blockQuotePrefix')
    } else if ((isListType(token.type) && token.direction === 'enter') || token.type === 'listItem') {
      // Nested lists are preceded by the list item's indentation, which causes an extra gap above the nested list item.
      // To avoid this, we 'consume' any immediately preceding indentation tokens by expanding the start
      // of the list and the listItem backwards and inserting it before the indent.
      consumePrecedingTokens(shiftedTokens, token, 'listItemIndent')
    } else {
      shiftedTokens.push(token)
    }
  }

  return shiftedTokens
}

/**
 * Consumes all immediately preceding tokens of the specified prefix type.
 * Returns the updated token and the consumed tokens.
 */
function consumePrecedingTokens(result: Token[], token: Token, prefix: 'blockQuotePrefix' | 'listItemIndent'): void {
  const consumedTokens: Token[] = []
  let consumedChars = 0
  const updatedToken = {...token}
  // looking backwards, pop off all immediately preceding prefixes
  while (result.at(-1)?.type === prefix) {
    consumedTokens.push(result.pop()!) // the exit token of the prefix

    // Consume all contents until we hit the enter token
    while (result.length && result.at(-1)?.type !== prefix) {
      consumedTokens.push(result.pop()!) // everything inside the prefix token (`>` or ` `)
    }

    const enterToken = result.pop()!
    consumedChars += enterToken.end - enterToken.start
    consumedTokens.push(enterToken) // the enter token of the prefix
  }

  updatedToken.start -= consumedChars
  consumedTokens.reverse()

  result.push(updatedToken, ...consumedTokens)
  // NOTE: This is technically incorrect because we did not modify the exit token. But we never
  // actually use the start/end positions of exit tokens, so it's fine.
}

/** Parse a Markdown string to a flat array of tokens. */
export function tokenize(markdown: string) {
  return postprocessTokens(
    micromarkPostprocess(micromarkParse.document().write(preprocess()(markdown, 'utf-8', true))).map<Token>(
      ([direction, token]) => ({
        direction,
        type: token.type,
        start: token.start.offset,
        end: token.end.offset,
      }),
    ),
  )
}
